import type { MetricItem } from "@/components/key-metrics"
import {
  EVALUATION_REFERENCE_DATE,
  getPastTerms,
  getTermRatingTrend,
  getTermResponseTrend,
  shortTermChartLabel,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import { daysUntil } from "@/lib/evaluation-term-utils"

/** Institutional response rate target for live evaluation windows. */
const RESPONSE_RATE_TARGET = 70

function countNeedsAction(currentTerm?: EvaluationTerm, lastTerm?: EvaluationTerm): number {
  let total = 0
  if (currentTerm) {
    total += currentTerm.counts.notSetup + currentTerm.counts.draft
  }
  if (lastTerm?.attention) {
    total +=
      lastTerm.attention.coursesBelowThreshold +
      lastTerm.attention.facultyRolesBelowThreshold
  }
  return total
}

function countBelowThreshold(lastTerm?: EvaluationTerm): number {
  if (!lastTerm?.attention) return 0
  return (
    lastTerm.attention.coursesBelowThreshold +
    lastTerm.attention.facultyRolesBelowThreshold
  )
}

/** Cumulative response curve for the open evaluation window (live term only). */
function liveWindowResponseSeries(currentRate: number): { label: string; value: number }[] {
  return [
    { label: "Day 1", value: Math.max(8, Math.round(currentRate * 0.22)) },
    { label: "Day 3", value: Math.max(12, Math.round(currentRate * 0.45)) },
    { label: "Day 5", value: Math.max(16, Math.round(currentRate * 0.72)) },
    { label: "Day 7", value: currentRate },
  ]
}

function daysToCloseLabel(currentTerm?: EvaluationTerm): string | undefined {
  if (!currentTerm?.nextClosingDate) return undefined
  const days = daysUntil(currentTerm.nextClosingDate, EVALUATION_REFERENCE_DATE)
  if (days < 0) return "Closing today"
  if (days === 0) return "Closes today"
  return `${days} days to close`
}

/**
 * Dashboard KPI strip — one visual type per tile:
 * - Needs action: count only (work queue, not a trend)
 * - Live evaluations: linear progress (live courses / total)
 * - Avg response: sparkline (cumulative during live window)
 * - Below threshold: count only (last term review flags)
 */
export function evaluationDashboardKpiMetrics(
  currentTerm?: EvaluationTerm,
  lastTerm?: EvaluationTerm,
): MetricItem[] {
  const needsAction = countNeedsAction(currentTerm, lastTerm)
  const liveCount = currentTerm?.counts.live ?? 0
  const courseTotal = currentTerm?.courseOfferings ?? 0
  const responseRate = currentTerm?.liveResponseRate
  const belowThreshold = countBelowThreshold(lastTerm)
  const daysToClose = daysToCloseLabel(currentTerm)
  const lastTermRate = lastTerm?.closedResponseRate
  const responseDelta =
    responseRate != null && lastTermRate != null
      ? `${responseRate - lastTermRate >= 0 ? "+" : ""}${responseRate - lastTermRate}%`
      : ""

  const notSetup = currentTerm?.counts.notSetup ?? 0
  const draft = currentTerm?.counts.draft ?? 0
  const setupGap = notSetup + draft
  const coursesBelow = lastTerm?.attention?.coursesBelowThreshold ?? 0
  const facultyBelow = lastTerm?.attention?.facultyRolesBelowThreshold ?? 0

  const thresholdBreakdown =
    coursesBelow > 0 || facultyBelow > 0
      ? [
          ...(coursesBelow > 0 ? [{ label: "Courses", value: coursesBelow }] : []),
          ...(facultyBelow > 0 ? [{ label: "Faculty", value: facultyBelow }] : []),
        ]
      : []

  return [
    {
      id: "needs-action",
      label: "Needs action",
      value: needsAction,
      delta: "",
      trend: needsAction > 0 ? "up" : "neutral",
      trendPolarity: "lower_is_better",
      alert: needsAction >= 5 ? "danger" : needsAction >= 1 ? "warning" : undefined,
      description:
        setupGap > 0 && belowThreshold > 0
          ? `${setupGap} setup · ${belowThreshold} review flags`
          : setupGap > 0
            ? `${setupGap} awaiting setup`
            : lastTerm?.termName
              ? `${belowThreshold} review flags on ${lastTerm.termName}`
              : undefined,
      href: "#eval-kanban-heading",
    },
    {
      id: "live",
      label: "Live evaluations",
      value: courseTotal > 0 ? `${liveCount}/${courseTotal}` : liveCount,
      delta: "",
      trend: "neutral",
      description:
        courseTotal > 0
          ? `${Math.round((liveCount / courseTotal) * 100)}% of courses live${daysToClose ? ` · ${daysToClose}` : ""}`
          : [currentTerm?.termName, daysToClose].filter(Boolean).join(" · "),
      href: "#eval-kanban-heading",
      progress: liveCount,
      progressMax: courseTotal > 0 ? courseTotal : undefined,
      progressTone: "brand",
    },
    {
      id: "response-rate",
      label: "Avg response",
      value: responseRate != null ? `${responseRate}%` : "None",
      delta: responseDelta,
      trend:
        responseRate == null || lastTermRate == null
          ? "neutral"
          : responseRate >= lastTermRate
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: [daysToClose, `target ${RESPONSE_RATE_TARGET}%`].filter(Boolean).join(" · "),
      href: "#eval-kanban-heading",
      chart:
        responseRate != null
          ? {
              kind: "sparkline",
              tone:
                responseRate >= RESPONSE_RATE_TARGET
                  ? "success"
                  : responseRate >= 50
                    ? "warning"
                    : "danger",
              series: liveWindowResponseSeries(responseRate),
            }
          : undefined,
    },
    {
      id: "below-threshold",
      label: "Below threshold",
      value: belowThreshold,
      delta: "",
      trend: belowThreshold > 0 ? "up" : "neutral",
      trendPolarity: "lower_is_better",
      alert: belowThreshold >= 3 ? "warning" : undefined,
      description:
        coursesBelow > 0 || facultyBelow > 0
          ? [
              coursesBelow > 0 ? `${coursesBelow} courses` : null,
              facultyBelow > 0 ? `${facultyBelow} faculty roles` : null,
              lastTerm?.termName ?? null,
            ]
              .filter(Boolean)
              .join(" · ")
          : lastTerm?.termName,
      href: lastTerm ? `#term-${lastTerm.id}` : "#eval-analysis-heading",
      chart:
        thresholdBreakdown.length > 1
          ? {
              kind: "bars",
              tone: "warning",
              series: thresholdBreakdown,
            }
          : undefined,
    },
  ]
}

export function termAnalyticsKpiMetrics(
  term: EvaluationTerm,
  comparisonTerms: EvaluationTerm[] = getPastTerms(),
): MetricItem[] {
  const ratings = term.ratings
  const responseRate = term.closedResponseRate

  const responseComparison = getTermResponseTrend({ includeCurrent: true, includeLast: true })
    .map(row => ({
      label: shortTermChartLabel(row.termName),
      value:
        row.timelinePosition === "current" && row.liveResponseRate != null
          ? row.liveResponseRate
          : (row.closedResponseRate as number),
    }))

  const priorTerm = comparisonTerms
    .filter(row => row.closedResponseRate != null)
    .at(-1)
  const responseDelta =
    responseRate != null && priorTerm?.closedResponseRate != null
      ? `${responseRate - priorTerm.closedResponseRate >= 0 ? "+" : ""}${responseRate - priorTerm.closedResponseRate}%`
      : ""

  const priorRating = comparisonTerms.filter(row => row.ratings?.programAverage != null).at(-1)
  const programDeltaValue =
    ratings?.programAverage != null && priorRating?.ratings?.programAverage != null
      ? ratings.programAverage - priorRating.ratings.programAverage
      : null
  const programDelta =
    programDeltaValue != null
      ? `${programDeltaValue >= 0 ? "+" : ""}${programDeltaValue.toFixed(1)}`
      : ""

  const courseDeltaValue =
    ratings?.courseAverage != null && priorRating?.ratings?.courseAverage != null
      ? ratings.courseAverage - priorRating.ratings.courseAverage
      : null
  const courseDelta =
    courseDeltaValue != null
      ? `${courseDeltaValue >= 0 ? "+" : ""}${courseDeltaValue.toFixed(1)}`
      : ""

  const facultyDeltaValue =
    ratings?.facultyAverage != null && priorRating?.ratings?.facultyAverage != null
      ? ratings.facultyAverage - priorRating.ratings.facultyAverage
      : null
  const facultyDelta =
    facultyDeltaValue != null
      ? `${facultyDeltaValue >= 0 ? "+" : ""}${facultyDeltaValue.toFixed(1)}`
      : ""

  return [
    {
      id: "program-avg",
      label: "Program avg",
      value: ratings?.programAverage.toFixed(1) ?? "None",
      delta: programDelta,
      trend:
        ratings?.programAverage == null || priorRating?.ratings?.programAverage == null
          ? "neutral"
          : ratings.programAverage >= priorRating.ratings.programAverage
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: priorRating ? `vs ${priorRating.termName}` : undefined,
    },
    {
      id: "course-avg",
      label: "Course avg",
      value: ratings?.courseAverage.toFixed(1) ?? "None",
      delta: courseDelta,
      trend:
        ratings?.courseAverage == null || priorRating?.ratings?.courseAverage == null
          ? "neutral"
          : ratings.courseAverage >= priorRating.ratings.courseAverage
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: priorRating ? `vs ${priorRating.termName}` : undefined,
    },
    {
      id: "faculty-avg",
      label: "Faculty avg",
      value: ratings?.facultyAverage.toFixed(1) ?? "None",
      delta: facultyDelta,
      trend:
        ratings?.facultyAverage == null || priorRating?.ratings?.facultyAverage == null
          ? "neutral"
          : ratings.facultyAverage >= priorRating.ratings.facultyAverage
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: priorRating ? `vs ${priorRating.termName}` : undefined,
    },
    {
      id: "response-rate",
      label: "Response rate",
      value: responseRate != null ? `${responseRate}%` : "None",
      delta: responseDelta,
      trend:
        responseRate == null || priorTerm?.closedResponseRate == null
          ? "neutral"
          : responseRate >= priorTerm.closedResponseRate
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: priorTerm ? `vs ${priorTerm.termName}` : undefined,
      alert: responseRate != null && responseRate < RESPONSE_RATE_TARGET ? "warning" : undefined,
      chart:
        responseComparison.length > 1
          ? {
              kind: "bars",
              tone:
                responseRate != null && responseRate >= RESPONSE_RATE_TARGET
                  ? "success"
                  : "warning",
              series: responseComparison,
            }
          : undefined,
    },
  ]
}

/** Three-tile strip for closed-term focus cards (response, course, faculty). */
export function evaluationFocusTermCardMetrics(term: EvaluationTerm): MetricItem[] {
  const metrics = termAnalyticsKpiMetrics(term)
  const byId = new Map(metrics.map(item => [item.id, item]))
  return ["response-rate", "course-avg", "faculty-avg"]
    .map(id => {
      const item = byId.get(id)
      if (!item) return null
      const { chart: _chart, ...rest } = item
      return rest
    })
    .filter((item): item is MetricItem => item != null)
}

export function buildTermResponseChartData(terms?: EvaluationTerm[]) {
  const source = terms ?? getTermResponseTrend()
  return source.map(term => ({
    name: shortTermChartLabel(term.termName),
    rate:
      term.timelinePosition === "current" && term.liveResponseRate != null
        ? term.liveResponseRate
        : (term.closedResponseRate as number),
  }))
}

export function buildTermScoreChartData(terms?: EvaluationTerm[]) {
  const source = terms ?? getTermRatingTrend()
  return source.map(term => ({
    name: shortTermChartLabel(term.termName),
    score: term.ratings!.programAverage,
  }))
}

function priorClosedTerm(
  focus: EvaluationTerm,
  pastTerms: EvaluationTerm[],
): EvaluationTerm | undefined {
  return [...pastTerms]
    .filter(term => term.startDate < focus.startDate && term.closedResponseRate != null)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .at(-1)
}

export function getClosedTermResponseTrend(
  term: EvaluationTerm,
  pastTerms: EvaluationTerm[] = getPastTerms(),
): {
  delta: string
  trend: "up" | "down" | "neutral"
  priorTermName?: string
} | null {
  const responseRate = term.closedResponseRate
  if (responseRate == null) return null

  const prior = priorClosedTerm(term, pastTerms)
  if (prior?.closedResponseRate == null) {
    return { delta: "", trend: "neutral", priorTermName: prior?.termName }
  }

  const diff = responseRate - prior.closedResponseRate
  return {
    delta: `${diff >= 0 ? "+" : ""}${diff}%`,
    trend: diff === 0 ? "neutral" : diff > 0 ? "up" : "down",
    priorTermName: prior.termName,
  }
}

/** Closed-term KPIs for the Analytics dashboard approach (not operational lifecycle tiles). */
export function evaluationAnalyticsApproachKpiMetrics(
  focusTerm?: EvaluationTerm,
  pastTerms: EvaluationTerm[] = getPastTerms(),
): MetricItem[] {
  if (!focusTerm) {
    return [
      {
        id: "terms-on-record",
        label: "Terms on record",
        value: pastTerms.length,
        delta: "",
        trend: "neutral",
        description: "Closed terms available for review",
      },
    ]
  }

  const prior = priorClosedTerm(focusTerm, pastTerms)
  const responseRate = focusTerm.closedResponseRate
  const ratings = focusTerm.ratings
  const coursesBelow = focusTerm.attention?.coursesBelowThreshold ?? 0
  const facultyBelow = focusTerm.attention?.facultyRolesBelowThreshold ?? 0
  const belowThreshold = coursesBelow + facultyBelow

  const responseDelta =
    responseRate != null && prior?.closedResponseRate != null
      ? `${responseRate - prior.closedResponseRate >= 0 ? "+" : ""}${responseRate - prior.closedResponseRate}%`
      : ""

  const programDeltaValue =
    ratings?.programAverage != null && prior?.ratings?.programAverage != null
      ? ratings.programAverage - prior.ratings.programAverage
      : null
  const programDelta =
    programDeltaValue != null
      ? `${programDeltaValue >= 0 ? "+" : ""}${programDeltaValue.toFixed(1)}`
      : ""

  const thresholdBreakdown =
    coursesBelow > 0 || facultyBelow > 0
      ? [
          ...(coursesBelow > 0 ? [{ label: "Courses", value: coursesBelow }] : []),
          ...(facultyBelow > 0 ? [{ label: "Faculty", value: facultyBelow }] : []),
        ]
      : []

  const closedTermCount =
    pastTerms.filter(term => term.closedResponseRate != null).length +
    (focusTerm.closedResponseRate != null ? 1 : 0)

  return [
    {
      id: "response-rate",
      label: "Response rate",
      value: responseRate != null ? `${responseRate}%` : "None",
      delta: responseDelta,
      trend:
        responseRate == null || prior?.closedResponseRate == null
          ? "neutral"
          : responseRate >= prior.closedResponseRate
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: prior ? `vs ${prior.termName}` : focusTerm.termName,
      alert: responseRate != null && responseRate < RESPONSE_RATE_TARGET ? "warning" : undefined,
      href: `#term-${focusTerm.id}`,
    },
    {
      id: "program-avg",
      label: "Program avg",
      value: ratings?.programAverage.toFixed(1) ?? "None",
      delta: programDelta,
      trend:
        ratings?.programAverage == null || prior?.ratings?.programAverage == null
          ? "neutral"
          : ratings.programAverage >= prior.ratings.programAverage
            ? "up"
            : "down",
      trendPolarity: "informational",
      description: prior ? `vs ${prior.termName}` : focusTerm.termName,
      href: `#term-${focusTerm.id}`,
    },
    {
      id: "below-threshold",
      label: "Below threshold",
      value: belowThreshold,
      delta: "",
      trend: belowThreshold > 0 ? "up" : "neutral",
      trendPolarity: "lower_is_better",
      alert: belowThreshold >= 3 ? "warning" : undefined,
      description: focusTerm.termName,
      href: `#term-${focusTerm.id}`,
      chart:
        thresholdBreakdown.length > 1
          ? {
              kind: "bars",
              tone: "warning",
              series: thresholdBreakdown,
            }
          : undefined,
    },
    {
      id: "terms-on-record",
      label: "Terms on record",
      value: closedTermCount,
      delta: "",
      trend: "neutral",
      description: "Closed terms in program history",
      href: "#eval-term-history",
    },
  ]
}

export function formatAnalyticVerdict(focusTerm: EvaluationTerm): string {
  const parts: string[] = [`${focusTerm.termName} closed`]
  if (focusTerm.closedResponseRate != null) {
    parts.push(`${focusTerm.closedResponseRate}% response`)
  }
  if (focusTerm.ratings?.programAverage != null) {
    parts.push(`${focusTerm.ratings.programAverage.toFixed(1)} program avg`)
  }
  const flags =
    (focusTerm.attention?.coursesBelowThreshold ?? 0) +
    (focusTerm.attention?.facultyRolesBelowThreshold ?? 0)
  if (flags > 0) {
    parts.push(`${flags} flagged below threshold`)
  }
  return parts.join(" · ")
}

export function mergeTermHistory(
  lastTerm: EvaluationTerm | undefined,
  pastTerms: EvaluationTerm[],
): EvaluationTerm[] {
  const byId = new Map<string, EvaluationTerm>()
  for (const term of pastTerms) byId.set(term.id, term)
  if (lastTerm) byId.set(lastTerm.id, lastTerm)
  return [...byId.values()].sort((a, b) => b.startDate.localeCompare(a.startDate))
}
